import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "../login/login.css";
import "./WaitingRoom.css";
import { subscribeHuntStart, registerHuntPlayer, subscribeHuntPlayers } from "../../utils/huntFirebase";
import { s3PublicUrl } from "../../utils/s3PublicUrl";

function readGameSettings() {
  try {
    return JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
  } catch {
    return {};
  }
}

function readUserData() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

/** Step 3 of 3 — holds every team here until the admin starts the hunt from Live Host. The
 * start signal comes over Firebase Realtime Database (see src/firebase.js) so every waiting
 * screen moves at the same instant instead of each polling and drifting apart. */
function WaitingRoom() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.auth?.user);
  const { data } = useSelector((state) => state.theme);

  const headingColor = data?.headingColor || "#D83307";
  const buttonBg = data?.buttonBgColor || "#D83307";
  const rawCover = data?.background_mob || data?.hunt_cover_image || data?.background_desk;
  const coverImage = rawCover ? s3PublicUrl(rawCover) : "";

  useEffect(() => {
    document.body.classList.add("login-static-bg");
    if (coverImage) {
      document.body.style.backgroundImage = `url("${encodeURI(coverImage)}")`;
    }
    return () => {
      document.body.classList.remove("login-static-bg");
      document.body.style.backgroundImage = "";
    };
  }, [coverImage]);

  const storedUserData = useMemo(readUserData, []);
  const gameSettings = useMemo(readGameSettings, []);

  const stageId = location.state?.stageId ?? gameSettings.stageId ?? null;
  const themeName = location.state?.themeName ?? gameSettings.themeName ?? "theme0";
  const teamNumber = gameSettings.team_number ?? "";
  const teamName = gameSettings.team_name ?? "";
  const warCry = gameSettings.war_cry ?? "";

  const organizationId = user?.organizationId ?? storedUserData.organizationId ?? "";
  const sessionId = user?.sessionId ?? storedUserData.sessionId ?? "";
  const uid = user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;

  const maxTeams = data?.numTeams ?? null;
  const [registeredCount, setRegisteredCount] = useState(null);

  // Register this team's presence in the Waiting Room (session/{org}__{session}/users/{uid}) so
  // the admin — and the "X of Y teams in" counter below — can see who's actually waiting live.
  useEffect(() => {
    if (!organizationId || !sessionId || !uid) return;
    registerHuntPlayer(organizationId, sessionId, uid, {
      userId: String(uid),
      teamNumber,
      teamName,
      status: "waiting",
    }).catch((e) => console.error("[WaitingRoom] failed to register presence:", e?.message || e));
  }, [organizationId, sessionId, uid, teamNumber, teamName]);

  // Live team count, driven by the same presence nodes above.
  useEffect(() => {
    if (!organizationId || !sessionId) return undefined;
    const unsubscribe = subscribeHuntPlayers(organizationId, sessionId, (players) => {
      setRegisteredCount(Object.keys(players || {}).length);
    });
    return () => unsubscribe();
  }, [organizationId, sessionId]);

  // Live "hunt started" flag — flipped by the admin's Publish button (or Live Host's
  // "Start hunt" button) the instant the hunt goes live.
  useEffect(() => {
    if (!organizationId || !sessionId) return undefined;
    const unsubscribe = subscribeHuntStart(organizationId, sessionId, (huntStart) => {
      if (huntStart?.startedAt) {
        navigate("/game", { replace: true, state: { stageId, themeName } });
      }
    });
    return () => unsubscribe();
  }, [organizationId, sessionId, navigate, stageId, themeName]);

  return (
    <div className="landing-page">
      <div className="waiting-room-blob waiting-room-blob--blue" />
      <div className="waiting-room-blob waiting-room-blob--purple" />

      <div className="landing-content waiting-room-content">
        <div className="waiting-room-header">
          <span className="waiting-room-registered">REGISTERED</span>
          {maxTeams != null && registeredCount != null && (
            <span className="waiting-room-count">
              {registeredCount} of {maxTeams} teams in
            </span>
          )}
        </div>

        <div className="waiting-room-team-card" style={{ borderColor: buttonBg }}>
          <div className="waiting-room-team-label">TEAM</div>
          <div className="waiting-room-team-number" style={{ color: buttonBg }}>
            {String(teamNumber || "--").padStart(2, "0")}
          </div>
          <div className="waiting-room-team-name">{teamName || "Your team"}</div>
          {warCry && <div className="waiting-room-team-cry">&ldquo;{warCry}&rdquo;</div>}
        </div>

        <div className="waiting-room-status">
          <h2 className="waiting-room-status-title" style={{ color: headingColor }}>
            Waiting for the host to start
          </h2>
          <p className="waiting-room-status-desc">
            Keep this screen open. Your first checkpoint appears the moment the hunt begins.
          </p>
        </div>
      </div>
    </div>
  );
}

export default WaitingRoom;
